import { IFareSelectorsBoolean } from '../../types';
import { TicketSelectorPanelProps } from '@modules/ticket-search/components/ticket-accordion-panel/ticketSelectorPanelProps.interface';
import { createSelector } from '@ngrx/store';
import { LoadingDetails, ITicketSelectorErrorValues } from '@nx-ng/types';
import { isNull, isUndefined, omitBy } from 'lodash-es';
import {
  CustomerType,
  FareRequest,
  FareSelectorRequest,
  TravelArea,
  TravelMethod,
  TravelTime,
} from '../../client/src';
import {
  getTicketAndFaresFeatureState,
  ITicketsAndFareState,
} from '../../reducers';
import { IFareSelectorsState } from '../../reducers/fare-selectors';
import { getLoadingDetails } from '@nx-ng/util/get-loading-details';
import { buildCustomerTypeOptions } from './util/format-customer-type-options';
import { formatFilterTicketDescription } from './util/format-ticket-filter-description';
import { buildTravelMethodOptions } from './util/format-travel-method-options';
import { buildTravelTimeOptions } from './util/format-travel-time-options';
import { buildTravelZoneOptions } from './util/format-travel-zone-options';
import { determineSelectorErrors } from './util/selector-errors';

export const selectFaresSelectorsState = createSelector(
  getTicketAndFaresFeatureState,
  (state: ITicketsAndFareState) => state?.fareSelectors
);

export const selectFareSelectorsLoadState = createSelector(
  selectFaresSelectorsState,
  (state: IFareSelectorsState) => state?.loaded
);

export const selectFareSelectorsLoading = createSelector(
  selectFaresSelectorsState,
  (state: IFareSelectorsState): LoadingDetails =>
    getLoadingDetails(state?.loaded, 'updating_search_criteria')
);

export const selectFareSelectorsErrors = createSelector(
  selectFaresSelectorsState,
  (state: IFareSelectorsState) => state?.error
);

export const selectFareSelectorList = createSelector(
  selectFaresSelectorsState,
  (state: IFareSelectorsState) => state?.fareSelectorList
);

export const selectTouchedFields = createSelector(
  selectFaresSelectorsState,
  (state: IFareSelectorsState): IFareSelectorsBoolean => state?.touched
);

export const selectInvalidFields = createSelector(
  selectFaresSelectorsState,
  (state: IFareSelectorsState): IFareSelectorsBoolean =>
    state?.invalidSelections
);

export const selectCustomerTypes = createSelector(
  selectFareSelectorList,
  (list) => list?.customerTypes
);

export const selectCustomerTypesForSelectionPage = createSelector(
  selectCustomerTypes,
  (customerTypes: CustomerType[] = []): TicketSelectorPanelProps =>
    buildCustomerTypeOptions(customerTypes)
);

export const selectZones = createSelector(
  selectFareSelectorList,
  (list) => list?.zones
);

export const selectSelectedValues = createSelector(
  selectFaresSelectorsState,
  (state: IFareSelectorsState) => state?.selections
);

export const selectZonesForSelectionPage = createSelector(
  selectZones,
  selectSelectedValues,
  (
    zones: TravelArea[] = [],
    values: Partial<FareRequest>
  ): TicketSelectorPanelProps => buildTravelZoneOptions(zones, values.zone)
);

export const selectTravelTimes = createSelector(
  selectFareSelectorList,
  (list) => list?.travelTimes
);

export const selectTravelTimesForSelectionPage = createSelector(
  selectTravelTimes,
  selectSelectedValues,
  (
    times: TravelTime[] = [],
    values: Partial<FareRequest>
  ): TicketSelectorPanelProps =>
    buildTravelTimeOptions(times, values.travelTime)
);

export const selectTravelMethods = createSelector(
  selectFareSelectorList,
  (list) => list?.travelMethods
);

export const selectTravelMethodsForSelectionPage = createSelector(
  selectTravelMethods,
  selectSelectedValues,
  (
    methods: TravelMethod[] = [],
    values: Partial<FareRequest>
  ): TicketSelectorPanelProps =>
    buildTravelMethodOptions(methods, values.travelMethod)
);

export const selectFareSelectorRequest = createSelector(
  selectSelectedValues,
  (state: Partial<FareRequest>) => {
    const fareSelectorRequest: FareSelectorRequest = {
      customerType: state.customerType,
      zone: state.zone,
    };

    return omitBy(
      fareSelectorRequest,
      isUndefined || isNull
    ) as FareSelectorRequest;
  }
);

export const selectErrors = createSelector(
  selectTouchedFields,
  selectInvalidFields,
  selectSelectedValues,
  (
    touchedFields: IFareSelectorsBoolean,
    invalidFields: IFareSelectorsBoolean,
    values: Partial<FareRequest>
  ): ITicketSelectorErrorValues =>
    determineSelectorErrors({
      touchedFields,
      invalidFields,
      values,
    })
);

export const selectCustomerTypeError = createSelector(
  selectErrors,
  (errors: ITicketSelectorErrorValues) => errors.customerType
);

export const selectZoneError = createSelector(
  selectErrors,
  (errors: ITicketSelectorErrorValues) => errors.zone
);

export const selectTravelMethodError = createSelector(
  selectErrors,
  (errors: ITicketSelectorErrorValues) => errors.travelMethod
);

export const selectTravelTimeError = createSelector(
  selectErrors,
  (errors: ITicketSelectorErrorValues) => errors.travelTime
);

export const selectSelectionsValid = createSelector(
  selectSelectedValues,
  selectInvalidFields,
  (
    values: Partial<FareRequest>,
    invalidFields: IFareSelectorsBoolean
  ): boolean =>
    values.customerType != undefined &&
    !invalidFields.customerType &&
    values.travelMethod != undefined &&
    !invalidFields.travelMethod &&
    values.travelTime != undefined &&
    !invalidFields.travelTime &&
    values.zone != undefined &&
    !invalidFields.zone
);

export const selectFilterDesciption = createSelector(
  selectSelectedValues,
  selectSelectionsValid,
  (values: Partial<FareRequest>, selectionsValid: boolean): string =>
    formatFilterTicketDescription({
      values,
      selectionsValid,
    })
);
